/// <reference types="user-agent-data-types" />

import { SubscriberProvider } from './enums/subscriber-provider.ts'
import { DevicePlacement } from './types/device-placement.ts'
import { ensureError } from './utils/ensure-error.ts'

/** @internal */
export class Env {
    private static _requires2StepCache?: boolean

    public static get _scope(): Window {
        return this._isInSourcelessIFrame ? top! : this._isInWorkerGlobalScope ? self : window
    }

    public static get _location(): Location {
        return this._scope.location
    }

    public static get _navigator(): Navigator {
        return this._scope.navigator
    }

    public static get _document(): Document {
        return this._scope.document
    }

    public static get _isInWorkerGlobalScope(): boolean {
        return self && 'WorkerGlobalScope' in self
    }

    public static get _isInSourcelessIFrame(): boolean {
        return (
            !this._isInWorkerGlobalScope &&
            window &&
            typeof window === 'object' &&
            !!top &&
            top !== window.self &&
            !!window.frameElement &&
            (window.frameElement as HTMLIFrameElement).src === 'about:blank'
        )
    }

    /**
     * Returns {@link DevicePlacement}
     */
    public static get _devicePlacement(): DevicePlacement {
        let deviceType: DevicePlacement

        if (this._navigator.userAgentData) {
            deviceType = this._navigator.userAgentData.mobile ? 'mobile' : 'desktop'
        } else {
            try {
                deviceType = this._scope.matchMedia('only screen and (max-device-width: 768px)').matches
                    ? 'mobile'
                    : 'desktop'
            } catch {
                const legacyDetectors = [/Android/i, /webOS/i, /iPhone|iPad|iPod/i, /Blackberry/i, /Windows Phone/i]
                deviceType = legacyDetectors.some((rgx) => rgx.test(this._navigator.userAgent)) ? 'mobile' : 'desktop'
            }
        }

        return deviceType
    }

    public static get _isSafari(): boolean {
        const nav = this._navigator

        // Exclude Chrome-on-iOS (CriOS), Firefox-on-iOS (FxiOS), and Opera wrappers
        // which use WebKit but are not Safari.
        const isNotWrappedBrowser =
            !nav.userAgent.match(/crios/i) &&
            !nav.userAgent.match(/fxios/i) &&
            !nav.userAgent.match(/Opera|OPT\//)

        if (!isNotWrappedBrowser) {
            return false
        }

        // window.safari exists in desktop Safari and iOS Safari tabs,
        // but is absent in iOS standalone (PWA) mode.
        if ('safari' in Env._scope) {
            return true
        }

        // iOS standalone PWA: window.safari is not exposed, but the browser is
        // still Safari. Detect via Apple vendor + standalone display mode.
        if (this._isStandalonePWA) {
            const isAppleVendor = 'vendor' in nav && !!nav.vendor.match(/apple/i)
            if (isAppleVendor) {
                return true
            }
        }

        return false
    }

    public static get _isStandalonePWA(): boolean {
        try {
            if ('standalone' in this._navigator && this._navigator.standalone === true) {
                return true
            }

            return this._scope.matchMedia('(display-mode: standalone)').matches
        } catch {
            return false
        }
    }

    /**
     * Determines whether a 2-step interaction process is required based on the user's browser.
     * Certain browsers, such as Safari, Firefox, and Edge, may require a 2-step interaction
     * for specific operations, while others like Chrome and Chromium-based browsers do not.
     *
     * The method first checks if the browser is Safari, then examines user agent data (UA-CH)
     * for more detailed browser identification. If UA-CH is unavailable, it falls back to
     * examining the user agent string for browser detection heuristics.
     *
     * @return {boolean} Returns `true` if a 2-step interaction is required, otherwise `false`.
     */
    public static get _requires2Step(): boolean {
        if (this._requires2StepCache !== undefined) {
            return this._requires2StepCache
        }

        if (this._isSafari) {
            this._requires2StepCache = true
            return this._requires2StepCache
        }

        const nav = this._navigator
        const uad = nav.userAgentData as NavigatorUAData | undefined
        if (uad && Array.isArray(uad.brands) && uad.brands.length > 0) {
            for (const entry of uad.brands) {
                const brand = (entry?.brand ?? '').toLowerCase()
                if (brand.includes('firefox') || brand.includes('edge')) {
                    this._requires2StepCache = true
                    return this._requires2StepCache
                }
            }

            this._requires2StepCache = false
            return this._requires2StepCache
        }

        const ua = nav.userAgent.toLowerCase() ?? ''
        if (
            (ua && ua.indexOf('firefox') !== -1) ||
            (ua && ua.indexOf(' edg/') !== -1) ||
            (ua && ua.indexOf(' microsoft edge') !== -1)
        ) {
            this._requires2StepCache = true
            return this._requires2StepCache
        }

        this._requires2StepCache = false
        return this._requires2StepCache
    }

    /**
     * Returns true if the {@link PushManager} object is available
     * in the current browser scope.
     *
     * **Safari**: PushManager support was only added in Safari 16
     * (2022-09-12) and later versions. All previous versions will
     * return false and should result in the use of {@link SubscriberProvider.APNS}.
     */
    public static get _isPushManagerInScope(): boolean {
        return 'PushManager' in this._scope
    }

    /**
     * Determines the current push provider protocol based
     * on the response of {@link _isPushManagerInScope}.
     *
     * When the {@link PushManager} object is present the
     * provider protocol used is {@link SubscriberProvider.VAPID}.
     * When not preset {@link SubscriberProvider.APNS} is used.
     */
    public static get _pushProvider(): SubscriberProvider {
        return (
            // WorkerGlobalScope can only be accessed in VAPID Web Push configuration
            !this._isInWorkerGlobalScope &&
                // Safari supports both
                this._isSafari &&
                // If PushManager is available we should default to using Web Push
                !this._isPushManagerInScope
                ? SubscriberProvider.APNS
                : SubscriberProvider.VAPID
        )
    }

    public static get _isLocal(): boolean {
        return this._location.hostname === 'localhost' || this._location.hostname === '127.0.0.1'
    }

    public static get _isHttps(): boolean {
        return this._location.protocol === 'https:'
    }

    public static get _isArrayFullySupported(): boolean {
        return 'Array' in this._scope && 'from' in Array && 'some' in []
    }

    public static _getIsUserAgentSupported(): [supported: boolean, reason?: string] {
        try {
            if (!('userAgent' in this._navigator)) {
                return [false, 'self.userAgent not accessible']
            }

            // Bot Detection
            const recognizedBotRegex =
                /bot|spider|crawl|xwarler|disqus|hatena|okhttp|headless|flipboard|linkcheck|coccoc|kddi|ichiro|libwww|wget|apache-http|miniflux|digg|bingpreview|sleuth|pcore|pingdom|snacktory|ning|opengraph|woorank|validator|python|steamchat|whatsapp|slurp|feedbin|appengine|php-curl|meta(insp|-ext)|internet-measure|gb\.gy|findlinks|heritrix|ec2link|facebook(ext|cat)|analyzer|ichigo|europarchive|curl|tiny\stiny|feedfetch|mediapartner|google(other|-(inspect|ext)|\sfav)/im

            if (recognizedBotRegex.test(this._navigator.userAgent)) {
                return [false, 'self.navigator.userAgent is recognizedBot']
            }

            if ('appVersion' in this._navigator && recognizedBotRegex.test(this._navigator.appVersion)) {
                return [false, 'self.navigator.appVersion is recognizedBot']
            }

            if ('appCodeName' in this._navigator && recognizedBotRegex.test(this._navigator.appCodeName)) {
                return [false, 'self.navigator.appCodeName is recognizedBot']
            }

            if ('product' in this._navigator && recognizedBotRegex.test(this._navigator.product)) {
                return [false, 'self.navigator.product is recognizedBot']
            }

            if ('vendor' in this._navigator && recognizedBotRegex.test(this._navigator.vendor)) {
                return [false, 'self.navigator.vendor is recognizedBot']
            }

            // Wrapper Apps
            const recognizedWrapperApps = /fb(ios|an)|instagram|twitter|pinterest|micromessenger/i

            if (recognizedWrapperApps.test(this._navigator.userAgent)) {
                return [false, 'self.navigator.userAgent is not supported']
            }

            return [true]
        } catch (err) {
            return [false, ensureError(err).message]
        }
    }

    public static _getIsEnvBaseSupported(): [supported: boolean, reason?: string] {
        try {
            if (!this._isLocal && !this._isHttps) {
                return [false, 'self.location.protocol is not secure context (http:)']
            }

            // Generics
            if (!('location' in this._scope)) {
                return [false, 'self.location not accessible']
            }

            if (!('navigator' in this._scope)) {
                return [false, 'self.navigator not accessible']
            }

            if (!('Notification' in this._scope)) {
                return [false, 'self.Notification not accessible']
            }

            // Array Support Detection
            if (!this._isArrayFullySupported) {
                return [false, 'required Array functionality not accessible']
            }

            // Host Requirements Detection
            if (!this._location.host) {
                return [false, 'self.location.host is undefined']
            }

            if (!this._location.hostname) {
                return [false, 'self.location.hostname is undefined']
            }

            try {
                const hostnameParts = this._location.hostname.split('')
                if (!hostnameParts.length) {
                    return [false, 'self.location.hostname is empty']
                }
            } catch (e) {
                return [false, 'self.location.hostname is not a valid String']
            }

            // UserAgent Support Detection
            const [isUASupported, uaUnsupportedReason] = this._getIsUserAgentSupported()
            if (!isUASupported) {
                return [false, uaUnsupportedReason]
            }

            // PushManager Support Detection
            if (!this._isPushManagerInScope) {
                // iDevice Detection - PushManager is only in scope if visitor is in PWA / standalone mode
                const iDeviceRegex = /iphone|ipad/i

                if (iDeviceRegex.test(this._navigator.userAgent)) {
                    return [false, 'self.navigator.userAgent is iDevice']
                }

                if ('platform' in this._navigator && iDeviceRegex.test(this._navigator.platform)) {
                    return [false, 'self.navigator.platform is iDevice']
                }

                return [false, 'self.PushManager is not accessible']
            }

            // PushSubscription Support Detection
            if (!('PushSubscription' in this._scope)) {
                return [false, 'self.PushSubscription is not accessible']
            }

            const PushSubscriptionCls = (this._scope as any).PushSubscription as typeof PushSubscription
            if (!PushSubscriptionCls?.prototype.hasOwnProperty('getKey')) {
                return [false, 'self.PushSubscription.prototype.getKey is not accessible']
            }

            // ServiceWorker Support Detection
            if (!('ServiceWorker' in this._scope)) {
                return [false, 'self.ServiceWorker is not accessible']
            }

            if (!('serviceWorker' in this._navigator)) {
                return [false, 'self.navigator.serviceWorker is not accessible']
            }

            if (!('ServiceWorkerRegistration' in this._scope)) {
                return [false, 'self.ServiceWorkerRegistration is not accessible']
            }

            const ServiceWorkerRegistrationCls = (this._scope as any)
                .ServiceWorkerRegistration as typeof ServiceWorkerRegistration
            if (!ServiceWorkerRegistrationCls?.prototype.hasOwnProperty('showNotification')) {
                return [false, 'self.ServiceWorkerRegistration.prototype.showNotification is not accessible']
            }
        } catch (err) {
            return [false, ensureError(err).message]
        }

        return [true]
    }
}
